// components/DoctorAbsentModal.tsx
import React, { useState, useEffect } from 'react';
import { Modal, Switch, Input, Button, Space, Typography, Spin, message } from 'antd';
import {
  WarningOutlined,
  SendOutlined,
  CheckCircleFilled,
  CloseCircleFilled,
  ExclamationCircleOutlined,
} from '@ant-design/icons';
import { getFirestore, doc, onSnapshot, updateDoc, increment } from 'firebase/firestore';

const { TextArea } = Input;
const { Text } = Typography;

const DEFAULT_MESSAGE = 'لن يحضر الطبيب اليوم';
const currentCairoDate = () => {
  const parts = new Intl.DateTimeFormat('en', {
    timeZone: 'Africa/Cairo', year: 'numeric', month: 'numeric', day: 'numeric',
  }).formatToParts(new Date());
  const value = (type: Intl.DateTimeFormatPartTypes) => parts.find((part) => part.type === type)?.value || '';
  return `${Number(value('year'))}-${Number(value('month'))}-${Number(value('day'))}`;
};
const normalizeDate = (value: unknown) => {
  const match = String(value || '').match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  return match ? `${Number(match[1])}-${Number(match[2])}-${Number(match[3])}` : '';
};

interface DoctorAbsentModalProps {
  visible: boolean;
  onCancel: () => void;
  doctorId: string | null;
}

const DoctorAbsentModal: React.FC<DoctorAbsentModalProps> = ({ visible, onCancel, doctorId }) => {
  const [isAbsent, setIsAbsent] = useState(false);
  const [notifMessage, setNotifMessage] = useState(DEFAULT_MESSAGE);
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [toggling, setToggling] = useState(false);
  const [sending, setSending] = useState(false);

  // Sync status from Firestore in real-time
  useEffect(() => {
    if (!visible || !doctorId) return;
    setLoadingStatus(true);
    const db = getFirestore();
    const unsubscribe = onSnapshot(doc(db, 'doctors', doctorId), (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.data();
        const today = currentCairoDate();
        const storedDate = normalizeDate(data?.doctorAnnouncementDate);
        const dailyStatus = data?.doctorDailyStatuses?.[today];
        const hasDailyAnnouncement = dailyStatus && [
          'announcementActive', 'announcementType', 'announcementMessage', 'announcementAbsent',
        ].some((field) => Object.prototype.hasOwnProperty.call(dailyStatus, field));
        const appliesToday = hasDailyAnnouncement || (storedDate ? storedDate === today : true);
        setIsAbsent(hasDailyAnnouncement ? dailyStatus.announcementAbsent === true : appliesToday && (data?.doctorAbsent ?? false));
        setNotifMessage((hasDailyAnnouncement ? dailyStatus.announcementMessage : '') || data?.doctorAbsentReason || DEFAULT_MESSAGE);
      } else {
        setIsAbsent(false);
        setNotifMessage(DEFAULT_MESSAGE);
      }
      setLoadingStatus(false);
    }, () => setLoadingStatus(false));
    return () => unsubscribe();
  }, [visible, doctorId]);

  // Close with warning if doctor is still absent
  const handleClose = () => {
    if (isAbsent) {
      Modal.confirm({
        title: 'الطبيب لا يزال غائباً',
        icon: <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />,
        content: 'المرضى يرون حالياً أن الطبيب لن يأتي اليوم. هل تريد الإغلاق دون تغيير الحالة؟',
        okText: 'إغلاق',
        cancelText: 'البقاء',
        okType: 'danger',
        onOk: onCancel,
        direction: 'rtl',
      });
    } else {
      onCancel();
    }
  };

  // Toggle: only updates the boolean — no notification sent
  const handleToggle = async (val: boolean) => {
    if (!doctorId) return;
    setToggling(true);
    try {
      await updateDoc(doc(getFirestore(), 'doctors', doctorId), {
        doctorAbsent: val,
        doctorAbsentReason: val ? (notifMessage || DEFAULT_MESSAGE) : '',
        doctorAnnouncementDate: currentCairoDate(),
        [`doctorDailyStatuses.${currentCairoDate()}.announcementActive`]: val,
        [`doctorDailyStatuses.${currentCairoDate()}.announcementType`]: val ? 'absent' : '',
        [`doctorDailyStatuses.${currentCairoDate()}.announcementMessage`]: val ? (notifMessage || DEFAULT_MESSAGE) : '',
        [`doctorDailyStatuses.${currentCairoDate()}.announcementAbsent`]: val,
      });
      if (!val) {
        message.success('تم تعيين الطبيب كحاضر — اختفى الإشعار من تطبيق المرضى');
        onCancel();
      }
    } catch {
      message.error('حدث خطأ، الرجاء المحاولة مرة أخرى');
    } finally {
      setToggling(false);
    }
  };

  // Send notification to all waiting patients — every click sends, no toggle required
  const handleSend = async () => {
    if (!doctorId) return;
    setSending(true);
    try {
      const msg = notifMessage.trim() || DEFAULT_MESSAGE;
      await updateDoc(doc(getFirestore(), 'doctors', doctorId), {
        doctorAbsent: true,
        doctorAbsentReason: msg,
        doctorAnnouncementDate: currentCairoDate(),
        [`doctorDailyStatuses.${currentCairoDate()}.announcementActive`]: true,
        [`doctorDailyStatuses.${currentCairoDate()}.announcementType`]: 'absent',
        [`doctorDailyStatuses.${currentCairoDate()}.announcementMessage`]: msg,
        [`doctorDailyStatuses.${currentCairoDate()}.announcementAbsent`]: true,
        doctorAbsentNotifySeq: increment(1),
      });
      message.success('✅ تم إرسال الإشعار لجميع المرضى');
    } catch {
      message.error('حدث خطأ، الرجاء المحاولة مرة أخرى');
    } finally {
      setSending(false);
    }
  };

  return (
    <Modal
      title={
        <Space>
          <WarningOutlined style={{ color: '#fa8c16' }} />
          <span>إشعار غياب الطبيب</span>
        </Space>
      }
      open={visible}
      onCancel={handleClose}
      footer={null}
      width={480}
      destroyOnClose
    >
      {loadingStatus ? (
        <div style={{ textAlign: 'center', padding: 32 }}>
          <Spin />
        </div>
      ) : (
        <Space direction="vertical" style={{ width: '100%' }} size={16}>

          {/* ── Big status banner ── */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            padding: '16px 20px',
            borderRadius: 10,
            background: isAbsent ? '#fff1f0' : '#f6ffed',
            border: `2px solid ${isAbsent ? '#ff4d4f' : '#52c41a'}`,
          }}>
            {isAbsent
              ? <CloseCircleFilled style={{ fontSize: 32, color: '#ff4d4f', flexShrink: 0 }} />
              : <CheckCircleFilled style={{ fontSize: 32, color: '#52c41a', flexShrink: 0 }} />
            }
            <div>
              <div style={{ fontSize: 16, fontWeight: 700, color: isAbsent ? '#cf1322' : '#237804' }}>
                {isAbsent ? 'الطبيب غائب اليوم' : 'الطبيب حاضر اليوم'}
              </div>
              <div style={{ fontSize: 12, color: '#595959', marginTop: 2 }}>
                {isAbsent
                  ? '⚠️ المرضى يرون الآن إشعار غياب الطبيب وقائمة الانتظار مخفية'
                  : '✅ المرضى يرون قائمة الانتظار بشكل طبيعي'}
              </div>
            </div>
          </div>

          {/* ── Toggle ── */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '12px 16px',
            background: '#fafafa',
            border: '1px solid #d9d9d9',
            borderRadius: 8,
          }}>
            <div>
              <Text strong>حالة الطبيب</Text>
              <br />
              <Text type="secondary" style={{ fontSize: 12 }}>
                {isAbsent
                  ? 'اضغط "حاضر" لإخفاء الإشعار فوراً — بدون إشعار للمرضى'
                  : 'اضغط "غائب" لإظهار الإشعار للمرضى — بدون إرسال push notification'}
              </Text>
            </div>
            <Switch
              checked={isAbsent}
              onChange={handleToggle}
              loading={toggling}
              checkedChildren="غائب"
              unCheckedChildren="حاضر"
              style={{ minWidth: 70 }}
            />
          </div>

          {/* ── Send notification (only when absent) ── */}
          {isAbsent && (
            <>
              <div>
                <Text strong style={{ display: 'block', marginBottom: 4 }}>
                  رسالة الإشعار
                </Text>
                <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 6 }}>
                  اختياري — يمكن تعديلها أو إرسالها كما هي
                </Text>
                <TextArea
                  value={notifMessage}
                  onChange={(e) => setNotifMessage(e.target.value)}
                  placeholder={DEFAULT_MESSAGE}
                  rows={2}
                  maxLength={200}
                  dir="rtl"
                />
              </div>

              <Button
                type="primary"
                danger
                icon={<SendOutlined />}
                loading={sending}
                onClick={handleSend}
                block
                size="large"
                style={{ height: 48, fontSize: 15 }}
              >
                إرسال إشعار لجميع المرضى
              </Button>
            </>
          )}

          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button onClick={handleClose}>إغلاق</Button>
          </div>

        </Space>
      )}
    </Modal>
  );
};

export default DoctorAbsentModal;
